import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ic, ICONS } from '../data/icons';
import { Card, CardHeader, Badge, Modal, toast, FormField, Input, Select } from '../components/shared';
import { enrollDevice, fetchMdmApps } from '../api/client';
import jsQR from 'jsqr';

export default function MDMScanner({ C }) {
  const navigate = useNavigate();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [cameraActive, setCameraActive] = useState(false);
  const [scannedToken, setScannedToken] = useState('');
  const [enrollForm, setEnrollForm] = useState({
    token: '',
    device_name: '',
    serial: '',
    udid: '',
    imei: '',
    phone_model: '',
    ios_version: 'iOS 17',
    dept: 'Genel'
  });
  const [loading, setLoading] = useState(false);
  const [enrollSuccess, setEnrollSuccess] = useState(null);
  const [mdmApps, setMdmApps] = useState([]);
  const [manualMode, setManualMode] = useState(false);

  // Kamera başlat
  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.onloadedmetadata = () => {
          videoRef.current.play();
          setCameraActive(true);
          scanQRCode();
        };
      }
    } catch (err) {
      toast.error('Kamera erişimi reddedildi: ' + err.message);
      setManualMode(true);
    }
  };

  // QR Kod tarama
  const scanQRCode = () => {
    if (!videoRef.current || !canvasRef.current || !cameraActive) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const video = videoRef.current;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height);

    if (code) {
      setScannedToken(code.data);
      setEnrollForm(f => ({ ...f, token: code.data }));
      stopCamera();
      toast.success('QR kodu başarıyla tarandı!');
    } else {
      requestAnimationFrame(scanQRCode);
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      videoRef.current.srcObject.getTracks().forEach(track => track.stop());
      setCameraActive(false);
    }
  };

  const handleEnroll = async () => {
    if (!enrollForm.token || !enrollForm.device_name || !enrollForm.serial) {
      toast.error('Token, cihaz adı ve seri numarası zorunlu');
      return;
    }

    setLoading(true);
    try {
      const response = await enrollDevice({
        token: enrollForm.token,
        device_name: enrollForm.device_name,
        serial: enrollForm.serial,
        udid: enrollForm.udid || null,
        imei: enrollForm.imei || null,
        phone_model: enrollForm.phone_model || null,
        ios_version: enrollForm.ios_version,
        dept: enrollForm.dept
      });

      setEnrollSuccess(response);
      toast.success(`Cihaz başarıyla kaydedildi! (${response.apps_assigned} uygulama atandı)`);

      // Formu sıfırla
      setTimeout(() => {
        setEnrollForm({
          token: '',
          device_name: '',
          serial: '',
          udid: '',
          imei: '',
          phone_model: '',
          ios_version: 'iOS 17',
          dept: 'Genel'
        });
        setScannedToken('');
        setEnrollSuccess(null);
      }, 3000);
    } catch (err) {
      toast.error(err.message || 'Kaydı oluşturalamadı');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => stopCamera();
  }, []);

  if (!cameraActive && !manualMode) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ background: 'linear-gradient(135deg,rgba(34,197,94,0.12),rgba(16,185,129,0.12))', border: `1px solid rgba(34,197,94,0.2)`, borderRadius: 14, padding: '20px', display: 'flex', alignItems: 'center', gap: 16 }}>
          <div style={{ width: 50, height: 50, borderRadius: 12, background: 'linear-gradient(135deg,#22c55e,#10b981)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Ic d={ICONS.phone} size={24} s='#fff' />
          </div>
          <div>
            <div style={{ fontSize: 17, fontWeight: 800, color: C.text }}>MDM Cihaz Kaydı</div>
            <div style={{ fontSize: 13, color: C.textSub, marginTop: 2 }}>Kurulu MDM uygulaması - QR tarama ile cihaz kaydı</div>
          </div>
        </div>

        <Card C={C}>
          <div style={{ padding: '40px', textAlign: 'center' }}>
            <div style={{ width: 80, height: 80, borderRadius: 16, background: C.accentBg, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
              <Ic d={ICONS.camera} size={40} s={C.accent} />
            </div>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text, marginBottom: 6 }}>Kamerayı Başlat</div>
            <div style={{ fontSize: 13, color: C.textSub, marginBottom: 20 }}>Yeni cihazdan çıkan QR kodunu taramak için kamerayı etkinleştir</div>
            <button
              onClick={startCamera}
              style={{
                padding: '10px 24px',
                borderRadius: 10,
                border: 'none',
                background: C.accent,
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                marginBottom: 12
              }}
            >
              <Ic d={ICONS.camera} size={16} s='#fff' />
              Kamerayı Aç
            </button>
            <div style={{ fontSize: 12, color: C.textFaint }}>
              veya
            </div>
            <button
              onClick={() => setManualMode(true)}
              style={{
                padding: '8px 16px',
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                background: 'transparent',
                color: C.textSub,
                fontSize: 13,
                cursor: 'pointer',
                marginTop: 12
              }}
            >
              Token'ı Manuel Gir
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Başlık */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: '0 0 4px 0', fontSize: 18, fontWeight: 800, color: C.text }}>Cihaz Kaydı</h2>
          <p style={{ margin: 0, fontSize: 13, color: C.textSub }}>QR tarama yöntemiyle cihaz bilgilerini girin</p>
        </div>
      </div>

      {cameraActive && !manualMode && (
        <Card C={C}>
          <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', background: '#000' }}>
            <video
              ref={videoRef}
              style={{
                width: '100%',
                height: 'auto',
                display: 'block'
              }}
            />
            <canvas ref={canvasRef} style={{ display: 'none' }} />
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 200,
              height: 200,
              border: `3px solid ${C.accent}`,
              borderRadius: 8,
              pointerEvents: 'none'
            }} />
          </div>
          <div style={{ padding: '16px', textAlign: 'center', fontSize: 13, color: C.textSub }}>
            📱 QR kodu tarama bölgesine hizalayın
          </div>
          <div style={{ padding: '0 16px 16px', display: 'flex', gap: 8 }}>
            <button
              onClick={stopCamera}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                background: 'transparent',
                color: C.textSub,
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Kamerayı Kapat
            </button>
            <button
              onClick={() => setManualMode(true)}
              style={{
                flex: 1,
                padding: '10px',
                borderRadius: 8,
                border: `1px solid ${C.border}`,
                background: 'transparent',
                color: C.textSub,
                cursor: 'pointer',
                fontWeight: 600
              }}
            >
              Manuel Gir
            </button>
          </div>
        </Card>
      )}

      {(manualMode || scannedToken) && (
        <Card C={C}>
          <CardHeader C={C} title='Cihaz Bilgilerini Tamamla' />
          <div style={{ padding: '20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
            {!scannedToken && (
              <FormField label='Enrollment Token' required C={C}>
                <Input
                  C={C}
                  value={enrollForm.token}
                  onChange={(e) => setEnrollForm(f => ({ ...f, token: e.target.value }))}
                  placeholder='Token yapıştır veya tarayı...'
                />
              </FormField>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label='Cihaz Adı' required C={C}>
                <Input
                  C={C}
                  value={enrollForm.device_name}
                  onChange={(e) => setEnrollForm(f => ({ ...f, device_name: e.target.value }))}
                  placeholder='iPhone Pro Max'
                />
              </FormField>
              <FormField label='Seri Numarası' required C={C}>
                <Input
                  C={C}
                  value={enrollForm.serial}
                  onChange={(e) => setEnrollForm(f => ({ ...f, serial: e.target.value.toUpperCase() }))}
                  placeholder='ABCD1234567890'
                />
              </FormField>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label='UDID (Opsiyonel)' C={C}>
                <Input
                  C={C}
                  value={enrollForm.udid}
                  onChange={(e) => setEnrollForm(f => ({ ...f, udid: e.target.value }))}
                  placeholder='Device Unique ID'
                />
              </FormField>
              <FormField label='IMEI (Opsiyonel)' C={C}>
                <Input
                  C={C}
                  value={enrollForm.imei}
                  onChange={(e) => setEnrollForm(f => ({ ...f, imei: e.target.value }))}
                  placeholder='IMEI Numarası'
                />
              </FormField>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <FormField label='Telefon Modeli' C={C}>
                <Input
                  C={C}
                  value={enrollForm.phone_model}
                  onChange={(e) => setEnrollForm(f => ({ ...f, phone_model: e.target.value }))}
                  placeholder='iPhone 15 Pro Max'
                />
              </FormField>
              <FormField label='iOS Versiyonu' C={C}>
                <Select
                  C={C}
                  value={enrollForm.ios_version}
                  onChange={(e) => setEnrollForm(f => ({ ...f, ios_version: e.target.value }))}
                >
                  <option value='iOS 17'>iOS 17</option>
                  <option value='iOS 16'>iOS 16</option>
                  <option value='iOS 15'>iOS 15</option>
                </Select>
              </FormField>
            </div>

            <FormField label='Departman' C={C}>
              <Select
                C={C}
                value={enrollForm.dept}
                onChange={(e) => setEnrollForm(f => ({ ...f, dept: e.target.value }))}
              >
                {['Mühendislik', 'Pazarlama', 'Satış', 'Finans', 'İK', 'Tasarım', 'Hukuk', 'Operasyon', 'Genel'].map(d => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </Select>
            </FormField>

            <button
              onClick={handleEnroll}
              disabled={loading || !enrollForm.token || !enrollForm.device_name || !enrollForm.serial}
              style={{
                padding: '12px 20px',
                borderRadius: 10,
                border: 'none',
                background: C.accent,
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading || !enrollForm.token ? 0.6 : 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                marginTop: 8
              }}
            >
              <Ic d={ICONS.check} size={16} s='#fff' />
              {loading ? 'Kaydediliyor...' : 'Cihazı Kaydet'}
            </button>
          </div>
        </Card>
      )}

      {/* Başarı Modali */}
      <Modal
        isOpen={!!enrollSuccess}
        onClose={() => setEnrollSuccess(null)}
        title='✅ Cihaz Başarıyla Kaydedildi'
        type='success'
        C={C}
        confirmLabel='Kapat'
        onConfirm={() => setEnrollSuccess(null)}
      >
        {enrollSuccess && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ padding: '12px 14px', background: C.surface2, borderRadius: 10, fontSize: 13 }}>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ color: C.text }}>Cihaz ID:</strong> {enrollSuccess.device_id}
              </div>
              <div>
                <strong style={{ color: C.text }}>Atanan Uygulamalar:</strong> {enrollSuccess.apps_assigned} adet
              </div>
            </div>
            <div style={{ fontSize: 13, color: C.textSub, lineHeight: '1.6' }}>
              📱 Cihaz başarıyla MDM profiline kaydedildi.<br />
              Zorunlu uygulamalar otomatik olarak başlanacak...
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
